import { personalizedInboxRootPath } from '@ms/yammer-web-support/dist/urls';
import { generatePath, matchPath } from 'react-router';

import type {
  PersonalizedInboxReadStateFilter,
  PersonalizedInboxRouteViewType,
  PersonalizedInboxTabKey,
} from './PersonalizedInboxPage.constants';
import {
  getPersonalizedInboxPathSegmentFromTabKey,
  getPersonalizedInboxViewTypeFromTabKey,
  personalizedInboxReadStateFilterTabKeys,
  personalizedInboxTabKeys,
} from './PersonalizedInboxPage.constants';
import type { ReadStateFilter } from './__generated__/PersonalizedInboxPageQuery.graphql';

export const personalizedInboxUnreadPathSegment = 'unread';

export const personalizedInboxMatchPath: string = `${personalizedInboxRootPath}/:tab?/:readState?`;

export interface PersonalizedInboxRoute {
  readonly path: string;
  readonly tabKey: PersonalizedInboxTabKey;
  readonly viewType: PersonalizedInboxRouteViewType;
  readonly readStateFilter: PersonalizedInboxReadStateFilter;
  readonly redirectPath?: string;
}

export const getPersonalizedInboxPathFromTabKey = (
  key: PersonalizedInboxTabKey,
  readStateFilter: ReadStateFilter = 'ALL'
): string => {
  const supportsUnread = readStateFilter === 'UNREAD' && personalizedInboxReadStateFilterTabKeys.includes(key);

  return generatePath(personalizedInboxMatchPath, {
    tab: getPersonalizedInboxPathSegmentFromTabKey(key),
    readState: supportsUnread ? personalizedInboxUnreadPathSegment : undefined,
  });
};

export const getPersonalizedInboxDefaultPath = (
  allUnreadCount: number,
  viewerCanAccessPrioritized: boolean
): string => {
  if (allUnreadCount > 0) {
    return getPersonalizedInboxPathFromTabKey('all', 'UNREAD');
  }

  return getPersonalizedInboxPathFromTabKey(viewerCanAccessPrioritized ? 'prioritized' : 'mentions');
};

const getPersonalizedInboxAllRoute = (tabKey: PersonalizedInboxTabKey): PersonalizedInboxRoute => ({
  path: getPersonalizedInboxPathFromTabKey(tabKey),
  tabKey,
  viewType: getPersonalizedInboxViewTypeFromTabKey(tabKey),
  readStateFilter: 'ALL',
});

const personalizedInboxRoutes: PersonalizedInboxRoute[] = [
  {
    ...getPersonalizedInboxAllRoute('all'),
    path: personalizedInboxRootPath,
    redirectPath: getPersonalizedInboxPathFromTabKey('all'),
  },
  ...personalizedInboxTabKeys.flatMap((tabKey): PersonalizedInboxRoute[] => {
    const route = getPersonalizedInboxAllRoute(tabKey);

    return personalizedInboxReadStateFilterTabKeys.includes(tabKey)
      ? [
          route,
          {
            ...route,
            path: getPersonalizedInboxPathFromTabKey(tabKey, 'UNREAD'),
            readStateFilter: 'UNREAD',
          },
        ]
      : [route];
  }),
];

const getSupportedPersonalizedInboxRoute = (pathname: string): PersonalizedInboxRoute | undefined =>
  personalizedInboxRoutes.find((route) =>
    matchPath(pathname, {
      path: route.path,
      exact: true,
    })
  );

export const getPersonalizedInboxRouteFromPathname = (pathname: unknown): PersonalizedInboxRoute | undefined => {
  if (typeof pathname !== 'string') {
    return undefined;
  }

  const supportedRoute = getSupportedPersonalizedInboxRoute(pathname);

  if (supportedRoute) {
    return supportedRoute;
  }

  const canonicalPath = pathname.replace(/\/$/, '').replace(`/${personalizedInboxUnreadPathSegment}`, '');
  const canonicalRoute = getSupportedPersonalizedInboxRoute(canonicalPath);

  if (canonicalRoute?.tabKey == null || personalizedInboxReadStateFilterTabKeys.includes(canonicalRoute.tabKey)) {
    return undefined;
  }

  return {
    ...canonicalRoute,
    redirectPath: canonicalPath,
  };
};

export const getPersonalizedInboxPathWithReadStateFilter = (
  pathname: string,
  readStateFilter: PersonalizedInboxReadStateFilter
): string => {
  const tabKey = getPersonalizedInboxRouteFromPathname(pathname)?.tabKey;

  return tabKey ? getPersonalizedInboxPathFromTabKey(tabKey, readStateFilter) : personalizedInboxRootPath;
};
